/**
 * Unified TTS playback ownership (V3-TTS-MANAGER).
 * One generation-safe speak/stop API for browser and provider TTS.
 */

import { API_BASE_URL } from '../config/api';
import {
  speakWithBuiltinBrowserVoice,
  resolveBrowserVoice,
} from '../utils/builtinBrowserVoice';
import { parseSpeechApiError } from '../utils/elevenLabsError';
import { getSpokenMessage, type VoiceMode } from '../utils/voiceModeMessages';
import {
  getVoiceSettings,
  type VoiceSettingsSnapshot,
} from '../utils/voiceSettingsStore';
import { isVoiceTtsProviderDisabled } from './killSwitches';

export type TtsMode = 'full' | 'basic' | 'silent';

export type SpeakOptions = {
  /** Override voice mode for this utterance. */
  mode?: TtsMode;
  language?: string;
  selectedVoiceId?: string;
  /** Prefer provider (ElevenLabs) when configured. Default true. */
  allowProvider?: boolean;
  /** Called when this generation's playback ends (success or stop). */
  onEnded?: () => void;
};

export type TtsManagerDeps = {
  getSettings?: () => Promise<VoiceSettingsSnapshot>;
  fetchSpeech?: (
    body: { text: string; voice_id?: string; language: string },
    signal: AbortSignal
  ) => Promise<{ audioUrl: string } | { fallback: true; message?: string }>;
  playAudioUrl?: (url: string, generation: number, signal: AbortSignal) => Promise<void>;
  speakBrowser?: (
    text: string,
    selectedVoiceId: string | undefined,
    lang: string,
    generation: number
  ) => Promise<void>;
  residualGuardMs?: number;
  now?: () => number;
};

const DEFAULT_RESIDUAL_GUARD_MS = 150;

/**
 * Single owner for assistant speech. Stale generations never play or stop newer audio.
 */
export class TtsManager {
  private generation = 0;
  private speaking = false;
  private currentAudio: HTMLAudioElement | null = null;
  private fetchAbort: AbortController | null = null;
  private releaseWaiters: Array<() => void> = [];
  private readonly deps: {
    getSettings: () => Promise<VoiceSettingsSnapshot>;
    fetchSpeech: NonNullable<TtsManagerDeps['fetchSpeech']>;
    playAudioUrl: NonNullable<TtsManagerDeps['playAudioUrl']>;
    speakBrowser: NonNullable<TtsManagerDeps['speakBrowser']>;
    residualGuardMs: number;
    now: () => number;
  };

  constructor(deps: TtsManagerDeps = {}) {
    this.deps = {
      getSettings: deps.getSettings ?? getVoiceSettings,
      fetchSpeech: deps.fetchSpeech ?? defaultFetchSpeech,
      playAudioUrl: deps.playAudioUrl ?? this.defaultPlayAudioUrl.bind(this),
      speakBrowser: deps.speakBrowser ?? this.defaultSpeakBrowser.bind(this),
      residualGuardMs: deps.residualGuardMs ?? DEFAULT_RESIDUAL_GUARD_MS,
      now: deps.now ?? (() => Date.now()),
    };
  }

  get isSpeaking(): boolean {
    return this.speaking;
  }

  getGeneration(): number {
    return this.generation;
  }

  /**
   * Speak text. Supersedes any in-flight playback.
   */
  async speak(text: string, options: SpeakOptions = {}): Promise<void> {
    const generation = ++this.generation;
    this.stopInternal('superseded', false);

    const settings = await this.deps.getSettings();
    if (generation !== this.generation) return;

    const mode = (options.mode ||
      (settings.voice_mode as TtsMode | undefined) ||
      'full') as TtsMode;

    if (mode === 'silent') {
      return;
    }

    const spokenText = getSpokenMessage(
      text,
      mode as VoiceMode,
      options.language || settings.default_language
    );
    if (!spokenText) return;
    if (generation !== this.generation) return;

    const lang = options.language || settings.default_language || 'en';
    const selectedVoiceId = options.selectedVoiceId ?? settings.selected_voice;
    const allowProvider =
      options.allowProvider !== false && !isVoiceTtsProviderDisabled();
    const useProvider =
      allowProvider &&
      (settings.voice_provider === 'elevenlabs' || settings.voice_provider === 'custom');
    const hasKey =
      Boolean(settings.elevenlabs_api_key?.trim()) ||
      Boolean(settings.has_elevenlabs_api_key);

    this.speaking = true;

    try {
      if (useProvider && hasKey) {
        const voiceId = settings.custom_voice_id || settings.elevenlabs_voice_id;
        this.fetchAbort = new AbortController();
        const signal = this.fetchAbort.signal;
        try {
          const result = await this.deps.fetchSpeech(
            {
              text: spokenText,
              voice_id: voiceId || undefined,
              language: lang,
            },
            signal
          );
          if (generation !== this.generation) return;
          if ('fallback' in result) {
            await this.deps.speakBrowser(spokenText, selectedVoiceId, lang, generation);
          } else {
            await this.deps.playAudioUrl(result.audioUrl, generation, signal);
          }
        } catch {
          if (generation !== this.generation) return;
          await this.deps.speakBrowser(spokenText, selectedVoiceId, lang, generation);
        }
      } else {
        await this.deps.speakBrowser(spokenText, selectedVoiceId, lang, generation);
      }
    } finally {
      if (generation === this.generation) {
        this.speaking = false;
        this.notifyRelease();
        options.onEnded?.();
      }
    }
  }

  /**
   * Stop current playback. Idempotent.
   */
  stop(_reason = 'stop'): void {
    this.stopInternal(_reason, true);
  }

  /**
   * Stop playback, wait for resource release, then apply residual-audio guard.
   * Safe to call before opening the microphone. Never blocks forever.
   */
  async prepareForCapture(timeoutMs = 2000): Promise<void> {
    this.stop('prepare-for-capture');
    const started = this.deps.now();
    await Promise.race([
      this.waitUntilQuiet(),
      sleep(timeoutMs),
    ]);
    const remainingGuard = Math.max(
      0,
      this.deps.residualGuardMs - (this.deps.now() - started)
    );
    if (remainingGuard > 0) {
      await sleep(remainingGuard);
    }
  }

  /**
   * Resolve language-specific browser voice (for tests / settings UI).
   */
  resolveVoice(
    selectedVoiceId: string | undefined,
    lang: string,
    availableVoices: SpeechSynthesisVoice[]
  ): SpeechSynthesisVoice | null {
    return resolveBrowserVoice(selectedVoiceId, lang, availableVoices);
  }

  private stopInternal(_reason: string, bumpGeneration: boolean): void {
    if (bumpGeneration) {
      this.generation += 1;
    }

    if (this.fetchAbort) {
      this.fetchAbort.abort();
      this.fetchAbort = null;
    }

    if (this.currentAudio) {
      const audio = this.currentAudio;
      this.currentAudio = null;
      try {
        audio.onended = null;
        audio.onerror = null;
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
      } catch {
        /* ignore */
      }
    }

    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      try {
        window.speechSynthesis.cancel();
      } catch {
        /* ignore */
      }
    }

    this.speaking = false;
    this.notifyRelease();
  }

  private waitUntilQuiet(): Promise<void> {
    if (!this.speaking && !this.currentAudio) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this.releaseWaiters.push(resolve);
    });
  }

  private notifyRelease(): void {
    const waiters = this.releaseWaiters;
    this.releaseWaiters = [];
    waiters.forEach((resolve) => resolve());
  }

  private async defaultPlayAudioUrl(
    url: string,
    generation: number,
    signal: AbortSignal
  ): Promise<void> {
    if (generation !== this.generation || signal.aborted) return;

    const audio = new Audio(url);
    this.currentAudio = audio;

    await new Promise<void>((resolve, reject) => {
      const onAbort = () => {
        cleanup();
        resolve();
      };
      const cleanup = () => {
        signal.removeEventListener('abort', onAbort);
        audio.onended = null;
        audio.onerror = null;
      };

      signal.addEventListener('abort', onAbort);
      audio.onended = () => {
        if (this.currentAudio === audio) {
          this.currentAudio = null;
        }
        cleanup();
        resolve();
      };
      audio.onerror = () => {
        if (this.currentAudio === audio) {
          this.currentAudio = null;
        }
        cleanup();
        reject(new Error('audio playback failed'));
      };

      void audio.play().catch((err) => {
        cleanup();
        reject(err);
      });
    });
  }

  private async defaultSpeakBrowser(
    text: string,
    selectedVoiceId: string | undefined,
    lang: string,
    generation: number
  ): Promise<void> {
    if (generation !== this.generation) return;
    speakWithBuiltinBrowserVoice(text, selectedVoiceId, lang);
    // Browser synthesis has no reliable ended promise across engines;
    // mark quiet shortly after enqueue so prepareForCapture can proceed.
    await sleep(50);
  }
}

async function defaultFetchSpeech(
  body: { text: string; voice_id?: string; language: string },
  signal: AbortSignal
): Promise<{ audioUrl: string } | { fallback: true; message?: string }> {
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
  const response = await fetch(`${API_BASE_URL}/voice/speech`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token || ''}`,
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const message = await parseSpeechApiError(response);
    throw new Error(message);
  }

  const data = await response.json();
  if (!data?.success && data?.fallback === 'builtin') {
    return { fallback: true, message: data.message || data.error };
  }
  const audioUrl = data?.audio_url;
  if (!audioUrl) throw new Error('No audio returned');
  return { audioUrl };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Process-wide singleton used by legacy speak wrappers. */
let sharedTtsManager: TtsManager | null = null;

export function getSharedTtsManager(): TtsManager {
  if (!sharedTtsManager) {
    sharedTtsManager = new TtsManager();
  }
  return sharedTtsManager;
}

/** Test-only: replace or clear the shared manager. */
export function setSharedTtsManagerForTests(manager: TtsManager | null): void {
  sharedTtsManager = manager;
}
